import type {Evaluator} from "@cm/graph-v3"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {visit} from "#template/template-node/visit-helpers"

// Wrapper-style operator: holds a product-pair (cross-dim input pair) as its `operator` field. The
// concrete dim-variant pins which input-pair types are accepted.
export type ProductOperatorParameters<TOperator> = {
    name: string
    operator: TOperator
}

// The empty (all-null) pair state a freshly-selected operator input is seeded with; the user then
// fills the four attribute slots in the inspector.
type EmptyProductOperatorPairParameters = {primaryAttributeA: null; attributeSetA: null; primaryAttributeB: null; attributeSetB: null}
const EMPTY_PRODUCT_OPERATOR_PAIR_PARAMETERS: EmptyProductOperatorPairParameters = {
    primaryAttributeA: null,
    attributeSetA: null,
    primaryAttributeB: null,
    attributeSetB: null,
}

// A pair-node class selectable as a product operator's `operator`: self-identifying via `getNodeClass()`
// and constructible in the empty state above.
export type ProductOperatorInputClass<TOperator> = {
    getNodeClass(): string
    new (options: {parameters: EmptyProductOperatorPairParameters}): TOperator
}

// One shared parameterized base — variants pin `T` and declare their dim-specific marker manually.
// `TOperator` is the concrete pair-node type accepted by the variant. Defaults are the broadest
// valid bounds, so callers that need to refer to "any product operator" can write
// `GeometryProductOperatorNode` without filling in all slots.
export abstract class GeometryProductOperatorNode<
    out T extends IndexingDim = IndexingDim,
    out TOperator extends EvaluableTemplateNode<GeometryAttributeSetData<T> | null> = EvaluableTemplateNode<GeometryAttributeSetData<T> | null>,
> extends GeometryAttributeSetNode<T, ProductOperatorParameters<TOperator>> {
    // The input-pair classes this variant accepts, in display order. Subclasses pin their dim's set.
    public abstract get availableInputClasses(): readonly ProductOperatorInputClass<TOperator>[]

    // Swap `operator` to a freshly-constructed (empty) input of `nodeClass`, if it is one of this
    // variant's accepted inputs and not already selected. No-op otherwise.
    public setOperatorInput(nodeClass: string): void {
        if (this.parameters.operator.nodeClass === nodeClass) return
        const InputClass = this.availableInputClasses.find((cls) => cls.getNodeClass() === nodeClass)
        if (!InputClass) return
        this.updateParameters({operator: new InputClass({parameters: EMPTY_PRODUCT_OPERATOR_PAIR_PARAMETERS})})
    }

    public override onFilterActive(
        context: OnFilterActiveContext,
        evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<ProductOperatorParameters<TOperator>> {
        visit(this.parameters.operator, evaluator)
        return super.onFilterActive(context, evaluator)
    }

    public override onCompile(context: OnCompileContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ProductOperatorParameters<TOperator>> {
        visit(this.parameters.operator, evaluator)
        return super.onCompile(context, evaluator)
    }

    protected override hasValidParameters(
        parameters: ProductOperatorParameters<TOperator>,
    ): parameters is ProductOperatorParameters<TOperator> & NonNullableParameters<ProductOperatorParameters<TOperator>> {
        return parameters.operator !== null && parameters.operator !== undefined
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<ProductOperatorParameters<TOperator>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const evaluatedGeometryData = evaluator.evaluate(parameters.operator)
        return {evaluatedGeometryData}
    }
}
